"use client";

import { useCallback, useRef, useState } from "react";
import { siteConfig } from "../lib/site-config";

export type CustomerChatMessage = {
  id: number;
  content: string;
  sender: "visitor" | "advisor";
  timestamp: Date;
};

export function useCustomerChat() {
  const messageId = useRef(1);
  const [messages, setMessages] = useState<CustomerChatMessage[]>([
    {
      id: messageId.current++,
      content: siteConfig.chat.introduction,
      sender: "advisor",
      timestamp: new Date(),
    },
  ]);
  const [isSending, setIsSending] = useState(false);

  const sendMessage = useCallback(
    async (content: string) => {
      const message = content.trim();
      if (!message) return;

      const visitorMessage: CustomerChatMessage = {
        id: messageId.current++,
        content: message,
        sender: "visitor",
        timestamp: new Date(),
      };
      const conversation = [...messages, visitorMessage];

      setMessages(conversation);
      setIsSending(true);

      try {
        const response = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            messages: conversation.map(({ content, sender }) => ({
              content,
              sender,
            })),
          }),
        });
        const data = (await response.json()) as { reply?: string };
        const reply = data.reply;

        if (!response.ok || !reply) throw new Error("Chat request failed");

        setMessages((current) => [
          ...current,
          {
            id: messageId.current++,
            content: reply,
            sender: "advisor",
            timestamp: new Date(),
          },
        ]);
      } catch {
        setMessages((current) => [
          ...current,
          {
            id: messageId.current++,
            content:
              "抱歉，当前暂时无法回复。请稍后重试，或通过官网微信、电话联系我们的增长顾问。",
            sender: "advisor",
            timestamp: new Date(),
          },
        ]);
      } finally {
        setIsSending(false);
      }
    },
    [messages],
  );

  return { messages, sendMessage, isSending };
}
